/**
 * ML Model Feature Flags Management UI
 * 
 * Allows platform admins to enable/disable ML models per tenant
 * for compliance, cost optimization, and gradual rollouts.
 */

import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { Flag, AlertCircle, CheckCircle, Clock, User } from 'lucide-react';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';

interface ModelFlag {
  model_id: string;
  model_name: string;
  model_type: string;
  model_version: string;
  is_enabled: boolean;
  enabled_by?: string;
  disabled_by?: string;
  disabled_at?: string;
  disabled_reason?: string;
  rollout_percentage: number;
  rollout_strategy: string;
}

interface TenantModelFlagsResponse {
  tenant_id: string;
  total_models: number;
  enabled_models: number;
  disabled_models: number;
  models: ModelFlag[];
}

const getApiConfig = () => ({
  authToken: localStorage.getItem('astrapeak_auth_token'),
  baseUrl: getBaseUrl()
});

export const ModelFeatureFlags: React.FC = () => {
  const [tenantId, setTenantId] = useState<string>('');
  const [data, setData] = useState<TenantModelFlagsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<ModelFlag | null>(null);
  const [disableReason, setDisableReason] = useState('');

  useEffect(() => {
    // Get tenant ID from URL or context
    const urlParams = new URLSearchParams(window.location.search);
    const tid = urlParams.get('tenant_id') || '';
    setTenantId(tid);

    if (tid) {
      fetchModelFlags(tid);
    }
  }, []);

  const fetchModelFlags = async (tid: string) => {
    setLoading(true);
    setError(null);

    try {
      const { authToken, baseUrl } = getApiConfig();
      const response = await fetch(
        `${baseUrl}/api/platform-admin/ml/feature-flags/tenant/${tid}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch model flags: ${response.statusText}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (model: ModelFlag) => {
    if (model.is_enabled) {
      // Disabling - show modal for reason
      setSelectedModel(model);
      setDisableModalOpen(true);
    } else {
      // Enabling - no reason needed
      await enableModel(model.model_id);
    }
  };

  const enableModel = async (modelId: string) => {
    try {
      const { authToken, baseUrl } = getApiConfig();
      const response = await fetch(
        `${baseUrl}/api/platform-admin/ml/feature-flags/tenant/${tenantId}/model/${modelId}/enable`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to enable model');
      }

      // Refresh data
      await fetchModelFlags(tenantId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable model');
    }
  };

  const disableModel = async () => {
    if (!selectedModel || !disableReason.trim()) {
      setError('Reason is required to disable a model');
      return;
    }

    try {
      const { authToken, baseUrl } = getApiConfig();
      const response = await fetch(
        `${baseUrl}/api/platform-admin/ml/feature-flags/tenant/${tenantId}/model/${selectedModel.model_id}/disable`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason: disableReason })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to disable model');
      }

      // Refresh data
      await fetchModelFlags(tenantId);
      
      // Close modal
      setDisableModalOpen(false);
      setSelectedModel(null);
      setDisableReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable model');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-gray-500 py-8">
        Select a tenant to view model feature flags
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Flag className="h-6 w-6 text-blue-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">ML Model Feature Flags</h1>
          </div>
          <div className="flex space-x-4 text-sm">
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
              <span className="text-gray-600">{data.enabled_models} Enabled</span>
            </div>
            <div className="flex items-center">
              <AlertCircle className="h-4 w-4 text-red-600 mr-1" />
              <span className="text-gray-600">{data.disabled_models} Disabled</span>
            </div>
          </div>
        </div>
      </div>

      {/* Models Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Model Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Version
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.models.map((model) => (
              <tr key={model.model_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{model.model_name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{model.model_type}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{model.model_version}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {model.is_enabled ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Enabled
                    </span>
                  ) : (
                    <div className="space-y-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Disabled
                      </span>
                      {model.disabled_reason && (
                        <div className="text-xs text-gray-500">
                          Reason: {model.disabled_reason}
                        </div>
                      )}
                      {model.disabled_by && (
                        <div className="text-xs text-gray-400 flex items-center">
                          <User className="h-3 w-3 mr-1" />
                          {model.disabled_by}
                          {model.disabled_at && (
                            <>
                              <Clock className="h-3 w-3 ml-2 mr-1" />
                              {new Date(model.disabled_at).toLocaleString()}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Switch
                    checked={model.is_enabled}
                    onChange={() => handleToggle(model)}
                    className={`${
                      model.is_enabled ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                  >
                    <span
                      className={`${
                        model.is_enabled ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Disable Modal */}
      {disableModalOpen && selectedModel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Disable Model: {selectedModel.model_name}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for disabling this model. This will be recorded in the audit trail.
            </p>
            <textarea
              value={disableReason}
              onChange={(e) => setDisableReason(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
              rows={4}
              placeholder="e.g., Model accuracy issues for this tenant, Compliance requirement, Cost optimization"
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => {
                  setDisableModalOpen(false);
                  setSelectedModel(null);
                  setDisableReason('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={disableModel}
                disabled={!disableReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Disable Model
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
